/**
 * CategoryBrowse — Blinkit / Zepto exact clone.
 *
 * Two modes, driven by URL:
 *   /category                    → Grid of parent categories only.
 *                                  No left rail, no chip strip, no in-page search.
 *   /category?category=<id>      → Detail view for that category.
 *                                  Left rail = ONLY that parent's sub-categories.
 *                                  Right = big product cards.
 *                                  No extra chip strip, no in-page search.
 *
 * All TEST_* seed data (name OR slug prefix) is filtered out from customer views.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useStore } from "@/contexts/StoreContext";
import ProductCard from "@/components/customer/ProductCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, PackageSearch } from "lucide-react";

// ─── Utilities ──────────────────────────────────────────────────────────────
const IS_TEST = (c) => /^TEST[_-]/i.test(c?.name || "") || /^TEST[_-]/i.test(c?.slug || "");

const gradientFor = (name = "") => {
  const palettes = [
    ["#FFE5D9", "#FFB68C"], ["#E6F4E5", "#8BD581"], ["#E5EBFF", "#8AA7FF"],
    ["#FFE9F3", "#FF9EC7"], ["#FFF3D1", "#FFC24A"], ["#E8F7F5", "#4CC7B5"],
    ["#F1E3FF", "#B87CFF"], ["#FFE1D5", "#FF7A4A"],
  ];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  const [a, b] = palettes[h % palettes.length];
  return `linear-gradient(135deg, ${a}, ${b})`;
};

// ─── Category tile (used in grid mode + sub-cat rail) ───────────────────────
function CategoryTile({ cat, thumb, size = "grid", active = false, onClick, fontCls = "font-medium" }) {
  const isRail = size === "rail";
  const boxCls = isRail
    ? `w-full aspect-square rounded-2xl grid place-items-center overflow-hidden ${active ? "ring-2 ring-primary shadow-md" : "ring-1 ring-transparent"}`
    : "w-full aspect-square rounded-2xl grid place-items-center overflow-hidden shadow-[0_2px_10px_-4px_rgba(0,0,0,0.08)]";
  return (
    <button
      onClick={onClick}
      className={`text-center flex flex-col items-center gap-1.5 group transition ${isRail ? "px-1 py-2" : "p-1"} ${active ? "text-primary" : "text-foreground/85"}`}
      data-testid={`cat-tile-${cat.id}`}
    >
      <div className={boxCls} style={{ background: gradientFor(cat.name || "") }}>
        {thumb
          ? <img src={thumb} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
          : <span className="text-3xl font-bold text-white/95 drop-shadow-sm">{(cat.name || "?").slice(0, 1).toUpperCase()}</span>}
      </div>
      <div className={`${isRail ? "text-[10.5px] leading-[1.15]" : "text-[12px] leading-tight"} line-clamp-2 px-0.5 ${fontCls}`}>
        {cat.name}
      </div>
    </button>
  );
}

// ─── Grid mode ──────────────────────────────────────────────────────────────
function CategoryGrid({ parents, autoThumb, onPick, fontCls }) {
  // Group by `section` field. Categories without a section go into a trailing
  // "More categories" bucket. Section order = first-appearance in sort_order
  // to give admins predictable control.
  const sections = useMemo(() => {
    const map = new Map();          // sectionName -> [cats]
    const unlabeled = [];
    for (const p of parents) {
      const s = (p.section || "").trim();
      if (!s) { unlabeled.push(p); continue; }
      if (!map.has(s)) map.set(s, []);
      map.get(s).push(p);
    }
    const result = [...map.entries()].map(([name, cats]) => ({ name, cats }));
    if (unlabeled.length > 0 && (result.length === 0 || map.size > 0)) {
      // Only show "More" header when at least one labeled section exists —
      // otherwise the whole grid is one flat list under the H1 already.
      if (result.length === 0) result.push({ name: "", cats: unlabeled });
      else result.push({ name: "More categories", cats: unlabeled });
    } else if (unlabeled.length > 0) {
      result.push({ name: "", cats: unlabeled });
    }
    return result;
  }, [parents]);

  return (
    <div className="max-w-[1400px] mx-auto px-3 md:px-6 py-4 md:py-6" data-testid="category-grid">
      <div className="mb-4">
        <h1 className="text-2xl md:text-3xl font-display font-semibold" data-testid="grid-heading">Shop by category</h1>
        <p className="text-xs md:text-sm text-muted-foreground mt-1">Fresh groceries, snacks &amp; essentials — delivered fast.</p>
      </div>

      {sections.map((sec, si) => (
        <div key={sec.name || "unlabeled"} className={si > 0 ? "mt-6 md:mt-8" : ""} data-testid={`grid-section-${si}`}>
          {sec.name && (
            <h2 className="text-lg md:text-xl font-display font-semibold mb-3 md:mb-4" data-testid={`grid-section-title-${si}`}>
              {sec.name}
            </h2>
          )}
          <div
            className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-3 md:gap-5"
            data-testid={si === 0 ? "grid-tiles" : `grid-tiles-${si}`}
          >
            {sec.cats.map((p) => (
              <CategoryTile
                key={p.id}
                cat={p}
                thumb={p.image_url || autoThumb[p.id]}
                size="grid"
                onClick={() => onPick(p)}
                fontCls={fontCls}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Detail mode ────────────────────────────────────────────────────────────
function CategoryDetail({ parent, parents, subs, activeSubId, drill, onPickParent, onPickSub, onBackToParents, products, prodLoading, autoThumb, user, fontCls }) {
  const nav = useNavigate();
  const railItems = drill === "subs" ? subs : parents;
  const railActiveId = drill === "subs" ? activeSubId : parent?.id;
  const hasRail = railItems.length > 0;
  const titleName = drill === "subs" ? parent.name : "Shop by category";

  return (
    <div className="max-w-[1400px] mx-auto px-2 md:px-3 py-2 md:py-3" data-testid="category-detail">
      {/* Compact top bar: back arrow (only in subs drill) + title */}
      <div className="flex items-center gap-2 mb-2 px-2 md:px-3" data-testid="detail-topbar">
        {drill === "subs" ? (
          <Button variant="ghost" size="icon" onClick={onBackToParents} data-testid="detail-back" className="rounded-full h-8 w-8">
            <ChevronLeft className="w-5 h-5" />
          </Button>
        ) : (
          <Button variant="ghost" size="icon" onClick={() => nav("/")} data-testid="detail-home" className="rounded-full h-8 w-8">
            <ChevronLeft className="w-5 h-5" />
          </Button>
        )}
        <div className="min-w-0">
          <h1 className="text-base md:text-2xl font-display font-semibold truncate leading-tight" data-testid="detail-title">
            {titleName}
          </h1>
        </div>
      </div>

      {/* Two-column layout: category rail (parents OR subs) + product grid */}
      <div className={`grid gap-0 ${hasRail ? "grid-cols-[74px_1fr] md:grid-cols-[96px_1fr]" : "grid-cols-1"}`}>
        {hasRail && (
          <aside
            className="bg-secondary/40 md:rounded-l-xl border border-r-0 md:border-border/60 sticky top-14 md:top-24 h-[calc(100vh-3.5rem)] md:h-[calc(100vh-7rem)] self-start overflow-y-auto no-scrollbar scroll-smooth"
            data-testid="detail-sub-rail"
          >
            {railItems.map((c, i) => {
              const active = c.id === railActiveId;
              const testId = drill === "subs" ? `detail-sub-${i}` : `detail-parent-${i}`;
              const handler = drill === "subs" ? () => onPickSub(c) : () => onPickParent(c);
              return (
                <button
                  key={c.id}
                  onClick={handler}
                  className={`w-full flex flex-col items-center gap-1 px-1 py-2 transition relative min-h-[76px] rounded-xl ${
                    active ? "bg-white text-primary font-semibold" : "hover:bg-white/60 text-foreground/70"
                  }`}
                  data-testid={testId}
                >
                  {active && (
                    <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] bg-primary rounded-r-full" />
                  )}
                  <div
                    className={`w-11 h-11 md:w-12 md:h-12 rounded-xl grid place-items-center shadow-[0_2px_6px_-2px_rgba(0,0,0,0.08)] overflow-hidden ${active ? "ring-1 ring-primary/60" : ""}`}
                    style={{ background: gradientFor(c.name || "") }}
                  >
                    {(c.image_url || autoThumb[c.id])
                      ? <img src={c.image_url || autoThumb[c.id]} alt="" className="w-full h-full object-cover" />
                      : <span className="text-base font-bold text-white/90 drop-shadow-sm">{(c.name || "?").slice(0, 1).toUpperCase()}</span>}
                  </div>
                  <div className={`text-[10px] leading-[1.15] text-center line-clamp-2 px-0.5 ${fontCls}`}>
                    {c.name}
                  </div>
                </button>
              );
            })}
          </aside>
        )}

        <section
          className={`min-w-0 bg-card ${hasRail ? "md:rounded-r-xl border md:border-l-0" : "rounded-xl border"} border-border/60 px-2 md:px-4 py-2 md:py-4`}
          data-testid="detail-products"
        >
          {/* No products at all */}
          {!user ? (
            <div className="rounded-xl border border-dashed p-8 text-center bg-secondary/30 mt-2" data-testid="detail-signin-cta">
              <PackageSearch className="w-6 h-6 mx-auto text-primary mb-2" />
              <div className="font-display text-lg font-semibold">Sign in to view products &amp; live prices</div>
              <Button asChild size="sm" className="mt-3 btn-primary-glow">
                <Link to={`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`}>Sign in</Link>
              </Button>
            </div>
          ) : prodLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="bg-card rounded-2xl border overflow-hidden">
                  <Skeleton className="aspect-square" />
                  <div className="p-3 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="rounded-xl border border-dashed p-10 text-center bg-secondary/30 mt-2" data-testid="detail-empty">
              <PackageSearch className="w-7 h-7 mx-auto text-muted-foreground mb-2" />
              <div className="font-display text-base font-semibold">No products in this category yet</div>
              <p className="text-xs text-muted-foreground mt-1">Come back soon or explore another category.</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => nav("/category")}>
                Browse all categories
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 md:gap-3.5" data-testid="detail-grid">
              {products.map((p) => (
                <div key={p.id} className="fade-in-up">
                  <ProductCard p={p} />
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

// ─── Main container ────────────────────────────────────────────────────────
export default function CategoryBrowse() {
  const { user } = useAuth();
  const { categoryFont } = useStore();
  const [params, setParams] = useSearchParams();
  const activeParentId = params.get("category") || "";

  // Sprint 28 — admin-controlled category label font
  const fontCls =
    categoryFont === "display"   ? "font-display italic" :
    categoryFont === "semibold"  ? "font-bold" :
    categoryFont === "condensed" ? "font-semibold tracking-tighter" :
    /* default */                  "font-medium";

  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [prodLoading, setProdLoading] = useState(false);
  const [autoThumb, setAutoThumb] = useState({});
  const [activeSubId, setActiveSubId] = useState("");
  const [drill, setDrill] = useState("parents");   // "parents" | "subs"
  const requestSeq = useRef(0);

  // Load categories once (filters TEST_ + inactive out).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/categories?limit=100");
        const live = (data.items || []).filter((c) => c.is_active !== false && !IS_TEST(c));
        setCats(live);
      } catch (e) {
        toast.error(errMsg(e, "Could not load categories"));
      } finally { setLoading(false); }
    })();
  }, []);

  const parents = useMemo(() => cats.filter((c) => !c.parent_id), [cats]);
  const activeParent = useMemo(() => parents.find((p) => p.id === activeParentId), [parents, activeParentId]);
  const subs = useMemo(
    () => (activeParent ? cats.filter((c) => c.parent_id === activeParent.id) : []),
    [cats, activeParent]
  );

  // Auto-open the first real parent whenever we land on /category without a selection.
  // This removes the "All" grid view and gives customers a direct product feed
  // (Blinkit-style — no browsing empty grids).
  useEffect(() => {
    if (!activeParentId && parents.length > 0) {
      setParams({ category: parents[0].id }, { replace: true });
    }
  }, [activeParentId, parents, setParams]);

  // Auto-select the first real sub-category whenever the parent changes and has subs.
  // No "All" pill — we always show a real sub's products.
  useEffect(() => {
    if (subs.length > 0) setActiveSubId(subs[0].id);
    else setActiveSubId("");
  }, [activeParentId, subs]);

  // Fetch products for the currently-active bucket.
  useEffect(() => {
    if (!activeParent || !user) { setProducts([]); return; }
    const catId = activeSubId || activeParent.id;
    const mySeq = ++requestSeq.current;
    setProdLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/products?category_id=${catId}&limit=60`);
        if (requestSeq.current !== mySeq) return;
        setProducts((data.items || []).filter((p) => !/^TEST[_-]/i.test(p.name || "")));
      } catch (e) {
        if (requestSeq.current !== mySeq) return;
        toast.error(errMsg(e, "Could not load products"));
        setProducts([]);
      } finally {
        if (requestSeq.current === mySeq) setProdLoading(false);
      }
    })();
  }, [activeParent, activeSubId, user]);

  // Auto-thumbnail: for any category without image_url, fetch first product's thumbnail.
  useEffect(() => {
    if (!user || cats.length === 0) return;
    const needy = cats.filter((c) => !c.image_url && autoThumb[c.id] === undefined);
    if (needy.length === 0) return;
    let cancelled = false;
    (async () => {
      const pairs = await Promise.all(
        needy.slice(0, 40).map(async (c) => {
          try {
            const { data } = await api.get(`/products?category_id=${c.id}&limit=1`);
            const first = (data.items || []).find((x) => x.thumbnail_url);
            return [c.id, first?.thumbnail_url || null];
          } catch { return [c.id, null]; }
        })
      );
      if (cancelled) return;
      const next = {};
      for (const [id, url] of pairs) next[id] = url || "";  // "" prevents refetch
      setAutoThumb((prev) => ({ ...prev, ...next }));
    })();
    return () => { cancelled = true; };
  }, [cats, user, autoThumb]);

  const openParent = (p) => {
    // Drill into the subs of this parent when it has any, else stay on parents rail.
    setParams({ category: p.id }, { replace: false });
    const nextSubs = cats.filter((c) => c.parent_id === p.id);
    if (nextSubs.length > 0) {
      setDrill("subs");
      setActiveSubId(nextSubs[0].id);
    } else {
      setDrill("parents");
      setActiveSubId("");
    }
  };
  const pickSub = (s) => setActiveSubId(s?.id || "");
  const backToParents = () => { setDrill("parents"); };

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-3 md:px-6 py-6" data-testid="cat-loading">
        <Skeleton className="h-8 w-64 mb-6" />
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="flex flex-col items-center gap-2">
              <Skeleton className="aspect-square rounded-2xl w-full" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return activeParent
    ? <CategoryDetail parent={activeParent} parents={parents} subs={subs}
                      activeSubId={activeSubId} drill={drill}
                      onPickParent={openParent} onPickSub={pickSub} onBackToParents={backToParents}
                      products={products} prodLoading={prodLoading} autoThumb={autoThumb} user={user} fontCls={fontCls} />
    : <CategoryGrid parents={parents} autoThumb={autoThumb} onPick={openParent} fontCls={fontCls} />;
}
